import { useEffect, useRef, useState } from 'react';
import { api } from './api.js';

interface RiskAwareness {
  effect: string; whyItMatters: string; whatTracksIt: string;
  managedWithClass: string | null; decisionWindow: string | null;
}
interface FirstAction { area: string; action: string; why: string }
interface Section { title: string; body: string }
interface Progress { step: string; ok: boolean; detail: string }

interface AnalysisRecord {
  id: string;
  kind: string;
  status: 'running' | 'done' | 'failed';
  headline: string | null;
  content_md: string | null;
  model: string | null;
  error: string | null;
  started_at: string;
  finished_at: string | null;
  sections_json: {
    startingPosition?: string; targetsExplained?: string;
    riskAwareness?: RiskAwareness[]; firstActions?: FirstAction[];
    doctorScript?: string[]; gaps?: string[]; sections?: Section[];
    progress?: Progress[]; riskScore?: number | null; riskBand?: string | null;
  } | null;
}

const STEP_LABEL: Record<string, string> = {
  meal_plan: 'Building your meal plan',
  doses: 'Scheduling your doses',
  bloods: 'Booking a baseline blood panel',
  risk: 'Computing your risk score',
  ai: 'Reading everything',
};

/**
 * The analysis.
 *
 * Polls while it runs rather than holding a request open for two minutes, and
 * shows which deterministic steps have already finished so the wait is legible
 * instead of a spinner. Those steps are done before the model is even called,
 * which is the point: if the AI fails you still have a plan.
 */
export function Analysis({ onDone }: { onDone?: () => void }): JSX.Element {
  const [a, setA] = useState<AnalysisRecord | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const timer = useRef<number | null>(null);

  useEffect(() => {
    let alive = true;
    const poll = (): void => {
      void api<AnalysisRecord | null>('/onboarding/analysis')
        .then((r) => {
          if (!alive) return;
          setA(r);
          if (r && r.status === 'running') {
            timer.current = window.setTimeout(poll, 4000);
          } else if (r && r.status === 'done') {
            onDone?.();
          }
        })
        .catch((e: Error) => { if (alive) setErr(e.message); });
    };
    poll();
    return () => {
      alive = false;
      if (timer.current) window.clearTimeout(timer.current);
    };
  }, [onDone]);

  if (err) return <div className="card"><p className="bad">{err}</p></div>;
  if (!a) return <div className="card"><p className="muted">No analysis yet.</p></div>;

  const s = a.sections_json ?? {};
  const progress = s.progress ?? [];

  if (a.status === 'running') {
    return (
      <div className="card">
        <h2>Analysing</h2>
        <p className="muted small">
          Reading your scan, your targets, your protocol and your answers together. A minute or
          two. You can leave this screen; it carries on without you.
        </p>
        <ul className="checklist">
          {Object.entries(STEP_LABEL).map(([key, label]) => {
            const done = progress.find((p) => p.step === key);
            return (
              <li key={key} className={done ? (done.ok ? 'ok' : 'bad') : ''}>
                <strong>{label}</strong>
                <span>{done ? done.detail : 'waiting'}</span>
              </li>
            );
          })}
        </ul>
      </div>
    );
  }

  if (a.status === 'failed') {
    return (
      <>
        <div className="card">
          <h2>The written analysis did not finish</h2>
          <p className="bad">{a.error}</p>
          <p className="muted small">
            Everything that does not need the model was still done. Your targets, meal plan, dose
            schedule and blood test booking are all in place; only the write-up is missing.
          </p>
          <button type="button" onClick={() => void api('/onboarding/analysis/rerun', { method: 'POST' }).then(() => window.location.reload())}>
            Try the write-up again
          </button>
        </div>
        {progress.length > 0 && <ProgressCard progress={progress} />}
      </>
    );
  }

  return (
    <>
      <div className="card">
        <h2>{a.headline}</h2>
        <p className="sub">
          {a.model} · {a.finished_at ? new Date(a.finished_at).toLocaleString('en-ZA', { hour12: false }) : ''}
          {s.riskScore != null ? ` · risk ${s.riskScore} (${s.riskBand})` : ''}
        </p>
        <p className="body">{a.content_md}</p>
      </div>

      {s.startingPosition && (
        <div className="card"><h2>Where you are starting</h2><p className="body">{s.startingPosition}</p></div>
      )}
      {s.targetsExplained && (
        <div className="card"><h2>What your targets mean</h2><p className="body">{s.targetsExplained}</p></div>
      )}

      {(s.firstActions ?? []).length > 0 && (
        <div className="card">
          <h2>Do these first</h2>
          <ol className="actions">
            {s.firstActions!.map((f, i) => (
              <li key={i}><strong>{f.action}</strong><p className="muted small">{f.why}</p></li>
            ))}
          </ol>
        </div>
      )}

      {(s.riskAwareness ?? []).length > 0 && (
        <div className="card">
          <h2>Worth knowing now</h2>
          <p className="muted small">
            What is documented to happen, what tracks it, and what class of thing is used to manage
            it. This is here so you know before it matters, not after. It is not a prescription and
            nothing here names a dose.
          </p>
          {s.riskAwareness!.map((r, i) => (
            <details key={i} className="risk">
              <summary>{r.effect}{r.decisionWindow ? ' · closing window' : ''}</summary>
              <p className="body">{r.whyItMatters}</p>
              <p className="muted small"><strong>Tracked by:</strong> {r.whatTracksIt}</p>
              {r.managedWithClass && <p className="muted small"><strong>Managed with:</strong> {r.managedWithClass}</p>}
              {r.decisionWindow && <p className="warn small"><strong>Window:</strong> {r.decisionWindow}</p>}
            </details>
          ))}
        </div>
      )}

      {(s.doctorScript ?? []).length > 0 && (
        <div className="card">
          <h2>What to say to a doctor</h2>
          <ul className="plain">{s.doctorScript!.map((d, i) => <li key={i}>{d}</li>)}</ul>
        </div>
      )}

      {(s.sections ?? []).map((sec, i) => (
        <div className="card" key={i}><h2>{sec.title}</h2><p className="body">{sec.body}</p></div>
      ))}

      {(s.gaps ?? []).length > 0 && (
        <div className="card muted-card">
          <h2>What it could not tell you</h2>
          <ul className="plain">{s.gaps!.map((g, i) => <li key={i}>{g}</li>)}</ul>
        </div>
      )}

      {progress.length > 0 && <ProgressCard progress={progress} />}
    </>
  );
}

function ProgressCard({ progress }: { progress: Progress[] }): JSX.Element {
  return (
    <div className="card muted-card">
      <h2>What was set up</h2>
      <ul className="checklist">
        {progress.map((p) => (
          <li key={p.step} className={p.ok ? 'ok' : 'bad'}>
            <strong>{STEP_LABEL[p.step] ?? p.step}</strong>
            <span>{p.detail}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
